import React, { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthShell, GoPalLogo } from './GoPalLogo';

const EyeOpen = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeClosed = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export function SetPasswordScreen({
  supabase, onDone,
}: { supabase: SupabaseClient; onDone: () => void }) {
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error,           setError]           = useState('');
  const [saving,          setSaving]          = useState(false);
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);

  const handleSubmit = async () => {
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must include a lowercase letter');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must include an uppercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must include a digit');
      return;
    }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setError('');
    setSaving(true);
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    if (err) { setError(err.message); setSaving(false); return; }
    window.location.hash = '';
    setSaving(false);
    onDone();
  };

  return (
    <AuthShell>
      <div className="text-center mb-5">
        <GoPalLogo />
        <h1 className="text-xl font-bold" style={{ letterSpacing: '-.02em', color: 'var(--text)', marginBottom: 4 }}>
          Welcome to GoPal
        </h1>
        <p className="text-sm text-subtle">Set a password so you can sign in anytime.</p>
      </div>

      {error && (
        <div
          className="text-xs mb-3"
          style={{
            color: 'var(--red)', background: 'var(--red-bg)',
            padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r)',
          }}
        >{error}</div>
      )}

      <div className="mb-3">
        <label className="field-label">New Password</label>
        <div className="relative">
          <input
            className="input"
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="At least 12 characters with uppercase, lowercase, and a digit"
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setShowNew(v => !v)}
            className="btn-ghost"
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 2, color: 'var(--text3)',
            }}
          >{showNew ? <EyeClosed /> : <EyeOpen />}</button>
        </div>
      </div>

      <div className="mb-5">
        <label className="field-label">Confirm Password</label>
        <div className="relative">
          <input
            className="input"
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            placeholder="Type it again"
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(v => !v)}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 2, color: 'var(--text3)',
            }}
          >{showConfirm ? <EyeClosed /> : <EyeOpen />}</button>
        </div>
      </div>

      <button
        className="btn btn-primary btn-lg w-full"
        disabled={saving}
        onClick={handleSubmit}
      >
        {saving ? 'Saving…' : 'Set Password & Continue'}
      </button>
    </AuthShell>
  );
}
